import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useConversionFunnel } from "@/hooks/useConversionFunnel";
import { Skeleton } from "@/components/ui/skeleton";
import { ArrowDown } from "lucide-react";

interface ConversionFunnelChartProps {
  startDate: string;
  endDate: string;
}

export const ConversionFunnelChart = ({ startDate, endDate }: ConversionFunnelChartProps) => {
  const { data: funnelData, isLoading } = useConversionFunnel({ startDate, endDate });

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Conversion Funnel</CardTitle>
          <CardDescription>User-Fluss durch die Conversion-Schritte</CardDescription>
        </CardHeader>
        <CardContent>
          <Skeleton className="h-[300px]" />
        </CardContent>
      </Card>
    );
  }

  if (!funnelData || funnelData.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Conversion Funnel</CardTitle>
          <CardDescription>User-Fluss durch die Conversion-Schritte</CardDescription>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">Keine Funnel-Daten verfügbar</p>
        </CardContent>
      </Card>
    );
  }

  const maxCount = Math.max(...funnelData.map(step => step.count));

  return (
    <Card>
      <CardHeader>
        <CardTitle>Conversion Funnel</CardTitle>
        <CardDescription>User-Fluss durch die Conversion-Schritte</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {funnelData.map((step, index) => {
          const widthPercentage = maxCount > 0 ? (step.count / maxCount) * 100 : 0;
          const showDropoff = index > 0 && step.dropoff > 0;

          return (
            <div key={step.name} className="space-y-2">
              {showDropoff && (
                <div className="flex items-center gap-2 text-sm text-muted-foreground pl-4">
                  <ArrowDown className="h-4 w-4 text-destructive" />
                  <span className="text-destructive font-medium">{step.dropoff}% Drop-off</span>
                </div>
              )}
              <div className="relative">
                <div 
                  className="h-12 bg-gradient-to-r from-primary to-primary/70 rounded-lg transition-all duration-500 flex items-center justify-between px-4"
                  style={{ width: `${Math.max(widthPercentage, 10)}%` }}
                >
                  <span className="text-sm font-medium text-primary-foreground">{step.name}</span>
                  <span className="text-sm font-bold text-primary-foreground">
                    {step.count.toLocaleString()}
                  </span>
                </div>
                {index > 0 && (
                  <div className="absolute right-4 -top-6 text-xs text-muted-foreground">
                    {step.percentage}% Conversion
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
};
